/**
 * Keyboard Shortcuts Overlay
 * Shown when the user presses `?` — lists all keyboard shortcuts.
 */

import React, { useEffect } from 'react';
import { CANVAS_THEME } from './canvas-theme';

export interface KeyboardShortcutsOverlayProps {
  onClose: () => void;
}

interface ShortcutEntry {
  key: string;
  description: string;
}

interface ShortcutGroup {
  label: string;
  shortcuts: ShortcutEntry[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    label: 'Navigation',
    shortcuts: [
      { key: 'Cmd+K', description: 'Open panel launcher' },
      { key: '?', description: 'Show this help' },
    ],
  },
  {
    label: 'Panels',
    shortcuts: [
      { key: 'T', description: 'Topics' },
      { key: 'N', description: 'Notes' },
      { key: 'C', description: 'Chat' },
      { key: 'A', description: 'AVID Channels' },
      { key: 'F', description: 'Forum' },
    ],
  },
  {
    label: 'Canvas',
    shortcuts: [
      { key: 'Scroll', description: 'Zoom in / out' },
      { key: 'Space+Drag', description: 'Pan canvas' },
    ],
  },
  {
    label: 'Notes',
    shortcuts: [
      { key: 'Cmd+N', description: 'New note' },
      { key: 'Cmd+F', description: 'Search notes' },
    ],
  },
];

export function KeyboardShortcutsOverlay({ onClose }: KeyboardShortcutsOverlayProps) {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: CANVAS_THEME.zIndex.modal,
      }}
    >
      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: '16px',
          border: CANVAS_THEME.panelBorder,
          boxShadow: CANVAS_THEME.shadowModal,
          width: '100%',
          maxWidth: '480px',
          padding: '24px',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: CANVAS_THEME.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: CANVAS_THEME.textSecondary,
              fontSize: '18px',
              lineHeight: 1,
              padding: '2px 6px',
              borderRadius: '6px',
              transition: CANVAS_THEME.transitionShift,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Two-column grid of groups */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
          }}
        >
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              {/* Group header */}
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: CANVAS_THEME.textSecondary,
                  marginBottom: '8px',
                }}
              >
                {group.label}
              </div>

              {/* Shortcut rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <kbd
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '5px',
                        background: 'rgba(0, 0, 0, 0.06)',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        color: CANVAS_THEME.textPrimary,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        lineHeight: '1.6',
                        boxShadow: '0 1px 0 rgba(0,0,0,0.08)',
                      }}
                    >
                      {shortcut.key}
                    </kbd>
                    <span
                      style={{
                        fontSize: '13px',
                        color: CANVAS_THEME.textSecondary,
                        lineHeight: '1.4',
                      }}
                    >
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
